import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { usePostHog } from 'hooks/usePostHogSafe';
import { selectCurrentUser } from '../ducks/user.duck';
import * as log from '../util/log';

/**
 * Utility function to identify user in PostHog
 * Exported for potential reuse in profile update handlers
 */
export const identifyPostHogUser = (posthog, user) => {
  if (!posthog || !user) return;

  const userId = user.id.uuid;
  const { email } = user.attributes;

  posthog.identify(userId, {
    email,
    app_version: __APP_VERSION__,
  });

  log.info('PostHog user identified', { userId, email, app_version: __APP_VERSION__ });
};

/**
 * Hook to sync PostHog identity with authentication state
 * Automatically identifies users on login/signup and resets on logout
 */
const usePostHogIdentity = () => {
  const posthog = usePostHog();
  const currentUser = useSelector(selectCurrentUser);
  const isLoggedInAs = useSelector((state) => state.auth.isLoggedInAs);
  const previousUserRef = useRef(null);

  useEffect(() => {
    // Skip PostHog tracking for super login / impersonation
    if (isLoggedInAs) {
      log.info('PostHog tracking enabled for logInAs!');
    }

    const previousUser = previousUserRef.current;
    const userChanged = previousUser?.id?.uuid !== currentUser?.id?.uuid;

    // Handle user login/signup (user went from null to populated, or user switched)
    if ((!previousUser && currentUser) || (previousUser && currentUser && userChanged)) {
      identifyPostHogUser(posthog, currentUser);
    }

    // Handle user logout (user went from populated to null)
    if (previousUser && !currentUser) {
      posthog.reset();
      log.info('PostHog user reset after logout');
    }

    // Update ref for next render
    previousUserRef.current = currentUser;
  }, [currentUser, posthog, isLoggedInAs]);
};

export default usePostHogIdentity;
